import {
  CHANGE_BASE_COMPARISON_SURVEY,
  CHANGE_DEPARTMENT_SEGMENT_GROUPS_SEARCH_KEYWORD,
  CLEAR_COMPARISON_SURVEY_CONDITION,
  FAILURE_FETCH_COMPARISON_SURVEY_DATA,
  FAILURE_FETCH_ENGAGEMENT_SURVEY_SEGMENTS,
  REMOVE_COMPARISON_SURVEY,
  REQUEST_FETCH_COMPARISON_SURVEY_DATA,
  REQUEST_FETCH_ENGAGEMENT_SURVEY_SEGMENTS,
  RESET_COMPARISON_SURVEY_DATA,
  SET_ACTIVE_ENGAGEMENT_SURVEY_SEGMENT,
  SET_COMPARISON_CURRENT_SURVEY,
  SET_FILTERED_DEPARTMENT_SEGMENT_GROUPS,
  SUCCESS_FETCH_COMPARISON_SURVEY_DATA,
  SUCCESS_FETCH_ENGAGEMENT_SURVEY_SEGMENTS,
  TOGGLE_ENGAGEMENT_SURVEY_SEGMENTS_GROUP_CHECK_BOX,
} from 'actions/actionTypes';
// import constants
import { ENGAGEMENT_SURVEY_SEGMENTS } from 'constants/surveys';
import {
  ComparisonActionTypes,
  ComparisonCategoryChildTypes,
  ComparisonCategoryDetailTypes,
  ComparisonQuestionTypes,
  ComparisonSegmentsType,
  ComparisonState,
  FormatComparisonSurveyDataTypes,
  FormatSegmentGroupTypes,
  FormatSegmentsTypes,
  SuccessFetchComparisonSurveyDataPayload,
} from 'types/backend/engagementSurvey/comparison';

export const initialState: ComparisonState = {
  isSegmentsLoading: false,
  isComparisonSurveyDataLoading: false,
  comparisonSurveyDataArray: [],
  comparisonSurvey: null,
  formatSegments: null,
  activeSegment: null,
  filteredActiveSegment: null,
  filteredDepartmentSegment: null,
  departmentSegmentGroupsSearchKeyword: '',
  currentSurvey: null,
  incrementNum: 1,
};

const filteringDepartmentSegmentGroupsByKeyword = (
  segmentGroups: FormatSegmentGroupTypes[],
  keyword: string,
) => {
  const optimizedKeyword = keyword.toLowerCase(); // 検索用に小文字に統一
  if (segmentGroups.length && optimizedKeyword.length) {
    const filterSegmentGroups = segmentGroups.filter((segmentGroup) => {
      const optimizedCompanyName = segmentGroup.name.toLowerCase();
      return optimizedCompanyName.includes(optimizedKeyword);
    });
    return filterSegmentGroups;
  }
  return segmentGroups;
};

// 比較項目テーブル用オブジェクト作成
export const createComparisonItem = (
  filterSegmentGroup: FormatSegmentGroupTypes[],
): ComparisonSegmentsType['segmentGroup'] => {
  const filteringSegmentGroup = (segmentType: string) => {
    return filterSegmentGroup.filter((segment) => segment.segmentType === segmentType);
  };
  return {
    department: filteringSegmentGroup('department'),
    position: filteringSegmentGroup('position'),
    age: filteringSegmentGroup('age'),
    employmentType: filteringSegmentGroup('employmentType'),
    recruitmentType: filteringSegmentGroup('recruitmentType'),
    gender: filteringSegmentGroup('gender'),
    seniority: filteringSegmentGroup('seniority'),
  };
};

// 比較列データセット
export const createComparisonSurveyData = (
  payload: SuccessFetchComparisonSurveyDataPayload,
  comparisonSegments: ComparisonSegmentsType,
  incrementNum: number,
  foundBaseComparisonSurveyData?: FormatComparisonSurveyDataTypes,
): FormatComparisonSurveyDataTypes => {
  const { comparisonSurveyDataPayload, comparisonSurveyResultRatioPayload } = payload;
  if (foundBaseComparisonSurveyData) {
    const categories = comparisonSurveyDataPayload.categories.map((category, categoryIndex) => {
      const categoryBaseDiff =
        category.score &&
        String(
          Number(category.score) -
            Number(foundBaseComparisonSurveyData.categories[categoryIndex].score),
        );
      const children: ComparisonCategoryChildTypes[] = category.children.map(
        (child, childIndex) => {
          const childBaseDiff =
            child.score &&
            String(
              Number(child.score) -
                Number(
                  foundBaseComparisonSurveyData.categories[categoryIndex].children[childIndex]
                    .score,
                ),
            );
          const questions: ComparisonQuestionTypes[] = child.questions.map(
            (question, questionIndex) => {
              const questionBaseDiff = String(
                Number(question.score) -
                  Number(
                    foundBaseComparisonSurveyData.categories[categoryIndex].children[childIndex]
                      .questions[questionIndex].score,
                  ),
              );
              return {
                ...question,
                baseDiff: questionBaseDiff,
              };
            },
          );
          return {
            ...child,
            baseDiff: childBaseDiff,
            questions,
          };
        },
      );
      return {
        ...category,
        baseDiff: categoryBaseDiff,
        children,
      };
    });
    const formatComparisonSurveyData: FormatComparisonSurveyDataTypes = {
      ...comparisonSurveyDataPayload,
      isBase: false,
      categories,
      comparisonSurveyResultRatio: comparisonSurveyResultRatioPayload,
      comparisonSegments,
      targetNum: incrementNum,
    };
    return formatComparisonSurveyData;
  }
  const categories = comparisonSurveyDataPayload.categories.map((category) => {
    const children: ComparisonCategoryChildTypes[] = category.children.map((child) => {
      const questions: ComparisonQuestionTypes[] = child.questions.map((question) => {
        return {
          ...question,
          baseDiff: null,
        };
      });
      return {
        ...child,
        baseDiff: null,
        questions,
      };
    });
    return {
      ...category,
      baseDiff: null,
      children,
    };
  });
  const formatComparisonSurveyData: FormatComparisonSurveyDataTypes = {
    ...comparisonSurveyDataPayload,
    isBase: true,
    categories,
    comparisonSurveyResultRatio: comparisonSurveyResultRatioPayload,
    comparisonSegments,
    targetNum: incrementNum,
  };
  return formatComparisonSurveyData;
};

// baseDataと比較
export const scoreDiffCalculation = (
  comparisonSurveyDataArray: FormatComparisonSurveyDataTypes[],
  baseData?: FormatComparisonSurveyDataTypes,
): FormatComparisonSurveyDataTypes[] => {
  const result = comparisonSurveyDataArray.map((comparisonSurveyData) => {
    if (!comparisonSurveyData.isBase && baseData) {
      const categories: ComparisonCategoryDetailTypes[] = comparisonSurveyData.categories.map(
        (category, categoryIndex) => {
          const categoryBaseDiff =
            Number(category.score) - Number(baseData.categories[categoryIndex].score);
          const children: ComparisonCategoryChildTypes[] = category.children.map(
            (child, childIndex) => {
              const childBaseDiff =
                Number(child.score) -
                Number(baseData.categories[categoryIndex].children[childIndex].score);
              const questions: ComparisonQuestionTypes[] = child.questions.map(
                (question, questionIndex) => {
                  const questionBaseDiff =
                    Number(question.score) -
                    Number(
                      baseData.categories[categoryIndex].children[childIndex].questions[
                        questionIndex
                      ].score,
                    );
                  return {
                    ...question,
                    baseDiff: questionBaseDiff.toString(),
                  };
                },
              );
              return {
                ...child,
                baseDiff: childBaseDiff.toString(),
                questions,
              };
            },
          );
          return {
            ...category,
            baseDiff: categoryBaseDiff.toString(),
            children,
          };
        },
      );
      return {
        ...comparisonSurveyData,
        categories,
      };
    }
    const categories: ComparisonCategoryDetailTypes[] = comparisonSurveyData.categories.map(
      (category) => {
        const children: ComparisonCategoryChildTypes[] = category.children.map((child) => {
          const questions: ComparisonQuestionTypes[] = child.questions.map((question) => {
            return {
              ...question,
              baseDiff: null,
            };
          });
          return {
            ...child,
            baseDiff: null,
            questions,
          };
        });
        return {
          ...category,
          baseDiff: null,
          children,
        };
      },
    );
    return {
      ...comparisonSurveyData,
      categories,
    };
  });
  return result;
};

const backendEngagementSurveyComparisonReducer = (
  state = initialState,
  action: ComparisonActionTypes,
): ComparisonState => {
  switch (action.type) {
    case REQUEST_FETCH_ENGAGEMENT_SURVEY_SEGMENTS: {
      const { comparisonSurvey } = action;
      return {
        ...state,
        isSegmentsLoading: true,
        comparisonSurvey,
      };
    }
    case SUCCESS_FETCH_ENGAGEMENT_SURVEY_SEGMENTS: {
      const { payload } = action;
      const formatSegments: FormatSegmentsTypes[] = payload.map((segment) => {
        const surveySegmentGroups = segment.surveySegmentGroups.map((segmentGroup) => {
          return {
            ...segmentGroup,
            checked: false,
          };
        });
        if (segment.segmentType === 'department') {
          return {
            ...segment,
            surveySegmentGroups,
            active: true,
          };
        }
        return {
          ...segment,
          surveySegmentGroups,
          active: false,
        };
      });

      const filteredDepartmentSegment = formatSegments.find((formatSegment) => {
        return formatSegment.segmentType === 'department';
      });

      // 並び替え
      const sortFormatSegments = ENGAGEMENT_SURVEY_SEGMENTS.map((surveySgments) => {
        const [result] = formatSegments.filter((formatSegment) => {
          return surveySgments === formatSegment.segmentType;
        });
        return result;
      });
      return {
        ...state,
        isSegmentsLoading: false,
        formatSegments: sortFormatSegments,
        activeSegment: sortFormatSegments[0],
        filteredActiveSegment: sortFormatSegments[0],
        filteredDepartmentSegment: filteredDepartmentSegment || null,
      };
    }
    case FAILURE_FETCH_ENGAGEMENT_SURVEY_SEGMENTS: {
      return {
        ...state,
        isSegmentsLoading: false,
      };
    }

    case SET_ACTIVE_ENGAGEMENT_SURVEY_SEGMENT: {
      const { id } = action;
      // null check
      if (!state.formatSegments) {
        return state;
      }
      // アクティブタブ変更 + 全segmentGroupのチェック状態更新
      const formatSegments = state.formatSegments.map((formatSegment) => {
        if (!state.activeSegment) return formatSegment; // null check
        if (state.activeSegment.id === id) {
          if (formatSegment.id === id) {
            return {
              ...formatSegment,
              active: true,
              surveySegmentGroups: state.activeSegment.surveySegmentGroups,
            };
          }
          return {
            ...formatSegment,
            active: false,
            surveySegmentGroups: formatSegment.surveySegmentGroups,
          };
        }
        if (formatSegment.id === id) {
          return {
            ...formatSegment,
            active: true,
            surveySegmentGroups: formatSegment.surveySegmentGroups,
          };
        }
        return {
          ...formatSegment,
          active: false,
          surveySegmentGroups: formatSegment.surveySegmentGroups,
        };
      });
      // 表示セグメントグループ変更
      const activeSegment = formatSegments.find((formatSegment) => {
        return formatSegment.id === id;
      });
      return {
        ...state,
        formatSegments,
        activeSegment: activeSegment || null,
        filteredActiveSegment: activeSegment || null,
      };
    }

    case TOGGLE_ENGAGEMENT_SURVEY_SEGMENTS_GROUP_CHECK_BOX: {
      // 再帰処理
      const cycle = (
        self: FormatSegmentGroupTypes,
        groups: FormatSegmentGroupTypes[],
        ary: FormatSegmentGroupTypes[] = [],
      ) => {
        ary.push(self);
        const filteredGroups = groups.filter((obj) => self.id === obj.parentId);
        filteredGroups.forEach((childObj) => {
          // パラメーターの更新をしているためlint errorをdisable
          childObj.checked = self.checked; // eslint-disable-line no-param-reassign
          cycle(childObj, groups, ary);
        });
        return ary;
      };

      const { target } = action;
      // null check
      if (!state.activeSegment || !state.formatSegments) {
        return state;
      }
      const { surveySegmentGroups } = state.activeSegment;
      const { formatSegments } = state;
      // segmentGroup抽出
      const targetSegmentGroup = surveySegmentGroups.find((segmentGroup) => {
        return segmentGroup.id.toString() === target.id;
      });

      if (!targetSegmentGroup) return state; // type guard
      // targetのチェック反転
      targetSegmentGroup.checked = !targetSegmentGroup.checked;
      // 再帰処理
      const childSegmentGroups = cycle(targetSegmentGroup, surveySegmentGroups);
      // 結合
      const joinChildSegmentGroups = [...surveySegmentGroups, ...childSegmentGroups];
      // 重複削除
      const updateChildSegmentGroups = joinChildSegmentGroups.filter(
        (newChildSegmentGroup, i, self) => {
          return self.indexOf(newChildSegmentGroup) === i;
        },
      );
      const activeSegment: FormatSegmentsTypes = {
        ...state.activeSegment,
        surveySegmentGroups: updateChildSegmentGroups,
      };
      const updateFormatSegments = formatSegments.map((formatSegment) => {
        if (formatSegment.id === activeSegment.id) {
          return {
            ...activeSegment,
          };
        }
        return formatSegment;
      });
      return {
        ...state,
        activeSegment,
        formatSegments: updateFormatSegments,
      };
    }

    // 部署検索 キーワードセット
    case CHANGE_DEPARTMENT_SEGMENT_GROUPS_SEARCH_KEYWORD: {
      const { departmentSegmentGroupsSearchKeyword } = action;
      return {
        ...state,
        departmentSegmentGroupsSearchKeyword,
      };
    }

    // 部署検索
    case SET_FILTERED_DEPARTMENT_SEGMENT_GROUPS: {
      const { departmentSegmentGroupsSearchKeyword } = action;
      if (!state.filteredDepartmentSegment || !state.activeSegment) return state;
      const { filteredDepartmentSegment } = state;
      const filteredDepartmentSegmentGroups = filteringDepartmentSegmentGroupsByKeyword(
        filteredDepartmentSegment.surveySegmentGroups,
        departmentSegmentGroupsSearchKeyword,
      );
      const activeSegment = {
        ...state.activeSegment,
        surveySegmentGroups: filteredDepartmentSegmentGroups,
      };
      return {
        ...state,
        filteredActiveSegment: activeSegment,
      };
    }

    case REQUEST_FETCH_COMPARISON_SURVEY_DATA: {
      return {
        ...state,
        isComparisonSurveyDataLoading: true,
      };
    }

    case SUCCESS_FETCH_COMPARISON_SURVEY_DATA: {
      const { payload } = action;
      const { comparisonSurveyDataArray, formatSegments, currentSurvey } = state;
      if (!formatSegments || !currentSurvey) return state;

      // checked value filtering
      const filterSegmentGroup = formatSegments.map((formatSegment) => {
        const filteredCechekedSegmentGroup = formatSegment.surveySegmentGroups.filter(
          (segmentGroup) => {
            return segmentGroup.checked;
          },
        );
        return filteredCechekedSegmentGroup;
      });
      const flatFilterSegmentGroup = filterSegmentGroup.reduce((acc, val) => acc.concat(val), []);

      // 比較項目オブジェクト作成
      const comparisonSegments: ComparisonSegmentsType = {
        date: currentSurvey.startAt,
        segmentGroup: createComparisonItem(flatFilterSegmentGroup),
      };

      // baseが設定されているか
      const foundBaseComparisonSurveyData = comparisonSurveyDataArray.find((survey) => {
        return survey.isBase;
      });

      const comparisonSurveyData: FormatComparisonSurveyDataTypes = {
        ...createComparisonSurveyData(
          payload,
          comparisonSegments,
          state.incrementNum,
          foundBaseComparisonSurveyData,
        ),
        isBase: !comparisonSurveyDataArray.length,
      };
      const updateComparisonSurveyDataArray = [...comparisonSurveyDataArray, comparisonSurveyData];

      return {
        ...state,
        isComparisonSurveyDataLoading: false,
        comparisonSurveyDataArray: updateComparisonSurveyDataArray,
        incrementNum: state.incrementNum + 1,
      };
    }

    case FAILURE_FETCH_COMPARISON_SURVEY_DATA: {
      return {
        ...state,
        isComparisonSurveyDataLoading: false,
      };
    }

    case SET_COMPARISON_CURRENT_SURVEY: {
      return {
        ...state,
        currentSurvey: action.survey,
      };
    }

    case CHANGE_BASE_COMPARISON_SURVEY: {
      const { index } = action;
      const comparisonSurveyDataArray = state.comparisonSurveyDataArray.map(
        (comparisonSurveyData, i) => {
          if (comparisonSurveyData.isBase) {
            return {
              ...comparisonSurveyData,
              isBase: false,
            };
          }
          if (index === i) {
            return {
              ...comparisonSurveyData,
              isBase: true,
            };
          }
          return comparisonSurveyData;
        },
      );
      const baseData = comparisonSurveyDataArray.find((comparisonSurveyData) => {
        return comparisonSurveyData.isBase;
      });
      if (baseData) {
        // 差分算出処理
        const updateComparisonSurveyDataArray: FormatComparisonSurveyDataTypes[] =
          scoreDiffCalculation(comparisonSurveyDataArray, baseData);
        const filterComparisonSurveyDataArray = updateComparisonSurveyDataArray.filter(
          (comparisonSurveyData) => {
            return !comparisonSurveyData.isBase;
          },
        );
        // baseDataを先頭に
        filterComparisonSurveyDataArray.unshift(baseData);
        return {
          ...state,
          comparisonSurveyDataArray: filterComparisonSurveyDataArray,
        };
      }

      return {
        ...state,
        comparisonSurveyDataArray,
      };
    }

    case REMOVE_COMPARISON_SURVEY: {
      const { index } = action;
      const comparisonSurveyDataArray = state.comparisonSurveyDataArray.filter(
        (comparisonSurveyData, i) => {
          return index !== i;
        },
      );
      const baseData = comparisonSurveyDataArray.find((comparisonSurveyData) => {
        return comparisonSurveyData.isBase;
      });
      const updateComparisonSurveyDataArray: FormatComparisonSurveyDataTypes[] =
        scoreDiffCalculation(comparisonSurveyDataArray, baseData);
      return {
        ...state,
        comparisonSurveyDataArray: updateComparisonSurveyDataArray,
      };
    }
    case CLEAR_COMPARISON_SURVEY_CONDITION: {
      const { formatSegments, activeSegment, filteredActiveSegment } = state;
      if (!formatSegments || !filteredActiveSegment || !activeSegment) return state;
      const updateSurveySegmentGroups = activeSegment.surveySegmentGroups.map(
        (surveySegmentGroup) => {
          return {
            ...surveySegmentGroup,
            checked: false,
          };
        },
      );
      const updateActiveSegment: FormatSegmentsTypes = {
        ...activeSegment,
        surveySegmentGroups: updateSurveySegmentGroups,
      };
      const updateFormatSegments = formatSegments.map((formatSegment) => {
        const updateFormatSurveySegmentGroups = formatSegment.surveySegmentGroups.map(
          (surveySegmentGroup) => {
            return {
              ...surveySegmentGroup,
              checked: false,
            };
          },
        );
        return {
          ...formatSegment,
          surveySegmentGroups: updateFormatSurveySegmentGroups,
        };
      });
      return {
        ...state,
        formatSegments: updateFormatSegments,
        activeSegment: updateActiveSegment,
        filteredActiveSegment: updateActiveSegment,
      };
    }

    case RESET_COMPARISON_SURVEY_DATA: {
      return initialState;
    }

    default: {
      return state;
    }
  }
};

export default backendEngagementSurveyComparisonReducer;
